/**
 * Every client's receivables, one row each — what's under contract, what's
 * been billed out of it, what's been paid, and what's still owed. Opening a
 * row is what ClientDetail is for: the draw schedule, change orders, and
 * invoices behind whichever of that client's projects you pick live there
 * instead of inline here, the same split Payables draws against a contractor.
 */
import { ZERO, add } from "@dantria/core";
import { useQuery } from "@tanstack/react-query";
import { api, fmtAmount as fmt, unwrap } from "../api";
import { Empty, Loading, Panel, Table, TableSearch, Td, Th, useTable } from "../ui";

export function Billing({
  slug,
  currency,
  onOpenClient,
}: {
  slug: string;
  currency: string;
  role: string;
  onOpenClient: (id: string) => void;
}) {
  const clients = useQuery({
    queryKey: ["clients", slug],
    queryFn: async () => unwrap(await api.api.c[":slug"].clients.$get({ param: { slug } })),
  });
  const projects = useQuery({
    queryKey: ["projects", slug],
    queryFn: async () => unwrap(await api.api.c[":slug"].projects.$get({ param: { slug } })),
  });
  const overview = useQuery({
    queryKey: ["reports-overview", slug],
    queryFn: async () =>
      unwrap(await api.api.c[":slug"].reports.overview.$get({ param: { slug }, query: {} })),
  });

  // Contract value (original plus approved change orders) lives on
  // `profitByProject`; invoiced/paid/outstanding come off the ledger-backed
  // rows. Both are keyed by project, so a project with a signed contract and
  // no billing activity yet still counts its full contract value.
  const contractByProject = new Map(
    (overview.data?.profitByProject ?? []).map((p) => [p.projectId, p.contractValue]),
  );
  const ledgerByProject = new Map((overview.data?.projects ?? []).map((p) => [p.projectId, p]));

  const totalsByClient = new Map<
    string,
    { count: number; contract: string; invoiced: string; paid: string; outstanding: string }
  >();
  for (const p of projects.data?.projects ?? []) {
    const ledger = ledgerByProject.get(p.id);
    const contract = contractByProject.get(p.id) ?? p.originalContractValue;
    const invoiced = ledger?.invoiced ?? ZERO;
    const paid = ledger?.cashIn ?? ZERO;
    const outstanding = ledger?.ar ?? ZERO;
    const t = totalsByClient.get(p.clientId);
    if (t) {
      t.count += 1;
      t.contract = add(t.contract, contract);
      t.invoiced = add(t.invoiced, invoiced);
      t.paid = add(t.paid, paid);
      t.outstanding = add(t.outstanding, outstanding);
    } else {
      totalsByClient.set(p.clientId, { count: 1, contract, invoiced, paid, outstanding });
    }
  }

  const rows = (clients.data?.clients ?? []).map((c) => {
    const t = totalsByClient.get(c.id) ?? {
      count: 0,
      contract: ZERO,
      invoiced: ZERO,
      paid: ZERO,
      outstanding: ZERO,
    };
    return { clientId: c.id, clientName: c.name, ...t };
  });

  const clientsTable = useTable(rows, {
    filter: (r, q) => r.clientName.toLowerCase().includes(q.toLowerCase()),
    sorters: {
      client: (r) => r.clientName.toLowerCase(),
      count: (r) => r.count,
      contract: (r) => Number(r.contract),
      billed: (r) => Number(r.invoiced),
      paid: (r) => Number(r.paid),
      outstanding: (r) => Number(r.outstanding),
    },
  });

  if (clients.isPending || !clients.data) return <Loading />;

  return (
    <Panel title="Billing">
      {rows.length === 0 ? (
        <Empty title="No clients yet">Add a client and a project to start billing.</Empty>
      ) : (
        <>
          <TableSearch
            value={clientsTable.query}
            onChange={clientsTable.setQuery}
            placeholder="Filter clients…"
          />
          <Table
            head={
              <>
                <Th
                  sortKey="client"
                  active={clientsTable.sortKey === "client"}
                  dir={clientsTable.dir}
                  onSort={clientsTable.toggleSort}
                >
                  Client
                </Th>
                <Th
                  right
                  sortKey="count"
                  active={clientsTable.sortKey === "count"}
                  dir={clientsTable.dir}
                  onSort={clientsTable.toggleSort}
                >
                  Projects
                </Th>
                <Th
                  right
                  sortKey="contract"
                  active={clientsTable.sortKey === "contract"}
                  dir={clientsTable.dir}
                  onSort={clientsTable.toggleSort}
                >
                  Contract
                </Th>
                <Th
                  right
                  sortKey="billed"
                  active={clientsTable.sortKey === "billed"}
                  dir={clientsTable.dir}
                  onSort={clientsTable.toggleSort}
                >
                  Billed
                </Th>
                <Th
                  right
                  sortKey="paid"
                  active={clientsTable.sortKey === "paid"}
                  dir={clientsTable.dir}
                  onSort={clientsTable.toggleSort}
                >
                  Paid
                </Th>
                <Th
                  right
                  sortKey="outstanding"
                  active={clientsTable.sortKey === "outstanding"}
                  dir={clientsTable.dir}
                  onSort={clientsTable.toggleSort}
                >
                  Outstanding
                </Th>
              </>
            }
          >
            {clientsTable.rows.map((r) => (
              <tr
                key={r.clientId}
                tabIndex={0}
                onClick={() => onOpenClient(r.clientId)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") onOpenClient(r.clientId);
                }}
                className="cursor-pointer border-b border-neutral-100 even:bg-neutral-50 transition-colors hover:bg-neutral-100 focus:bg-neutral-100 focus:outline-none"
              >
                <Td>{r.clientName}</Td>
                <Td right mono muted>
                  {r.count}
                </Td>
                <Td right mono>
                  {fmt(r.contract, currency)}
                </Td>
                <Td right mono muted>
                  {fmt(r.invoiced, currency)}
                </Td>
                <Td right mono>
                  {fmt(r.paid, currency)}
                </Td>
                <Td right mono>
                  {fmt(r.outstanding, currency)}
                </Td>
              </tr>
            ))}
          </Table>
        </>
      )}
    </Panel>
  );
}
